import React, { FC, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
	Button,
	ButtonGroup,
	Checkbox,
	FormControl,
	FormControlLabel,
	FormGroup,
	Grid,
	InputLabel,
	makeStyles,
	MenuItem,
	Select,
	TextField
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import dayjs from 'dayjs';
import ClearIcon from '@material-ui/icons/Clear';
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank';
import CheckBoxIcon from '@material-ui/icons/CheckBox';

import type { RootState } from '../../store/store';
import {
	name as reportFacetSelector,
	setSportFacet,
	setLeagueFacet,
	setStartTimeFacet,
	setEndTimeFacet,
	setTimesFacet,
	setStatusFacet,
	toggleHasAiringsFacet,
	resetAllFacets,
	toggleAutoRefetch
} from '../../store/reducers/reportFacet';
import { League } from '../../utils/types';
import { fetchAppConfigByProfileName } from '../common/Appconfig';

const useStyles = makeStyles((theme) => ({
	filter: {
		marginBottom: theme.spacing(2)
	},
	text: {
		width: 140
	},
	select: {
		minWidth: 150
	},
	date: {
		maxWidth: 150
	},
	segmentedInputText: {
		maxWidth: 150,
		'& .MuiInputBase-formControl': {
			borderTopRightRadius: 0,
			borderBottomRightRadius: 0
		}
	},
	segmentedInputButton: {
		backgroundColor: '#e0e0e0'
	},
	facetButton: {
		height: '2.5rem',
		'& .MuiSvgIcon-root': {
			marginRight: 5
		}
	},
	refetchToggle: {
		marginLeft: 1.5,
		'& .MuiCheckbox-root': {
			marginRight: 0,
			paddingRight: 5
		}
	}
}));

const ReportFilters: FC = () => {
	const classes = useStyles();
	const dispatch = useDispatch();

	// All these search facets come from the reportFacetSelector in the redux store
	const { sport, startTime, endTime, hasAirings, status, league, autoRefetch } = useSelector((state: RootState) => state[reportFacetSelector]);

	const hasLeague = league !== null;
	const hasFacets = sport || league || startTime || endTime || status || hasAirings === true || autoRefetch === false;
	const hasReversedDates = startTime && endTime && dayjs(startTime).isAfter(dayjs(endTime));

	const [leagues, setLeagues] = useState<string[]>([]);
	const [sports, setSports] = useState<string[]>([]);

	useEffect(() => {
		const getAppConfigs = async (): Promise<void> => {
			const leaguesConfig = await fetchAppConfigByProfileName('leagues');

			if (leaguesConfig) {
				const leagues = Object.values(JSON.parse(leaguesConfig) as Record<string, League>);
				setLeagues(
					leagues
						.filter((league: League) => {
							if (typeof league.displayName === 'undefined') return false;
							if (league.sport === 'Motorsport') league.sport = 'F1';
							else if (league.season?.discipline) league.sport = league.season.discipline;
							if (sport && sport !== league.sport) return false;
							return true;
						})
						.map((league: League) => league.displayName!)
						.sort() ?? []
				);
				const sports = new Set();
				leagues.forEach((league) => sports.add(league.sport));
				setSports((Array.from(sports) as string[]).sort());
			}
		};
		getAppConfigs();
	}, [sport]);

	return (
		<>
			<Grid container spacing={1} className={classes.filter} alignItems='center'>
				<Grid item>
					<FormControl variant='outlined' size='small'>
						<InputLabel id='sport-label'>Sport</InputLabel>
						<Select
							labelId='sport-label'
							id='sport'
							data-testid='games-filters-sport'
							label='SPORT'
							name='sport'
							disabled={hasLeague}
							value={sport || ''}
							onChange={({ target: { value } }): object => dispatch(setSportFacet(value || null))}
							className={classes.select}>
							<MenuItem value=''>
								<em>None</em>
							</MenuItem>
							{sports?.map((sport: string, idx: number): JSX.Element => {
								let displaySport = sport;
								if (sport === 'AmericanFootball') {
									displaySport = 'American Football';
								}
								return (
									<MenuItem value={sport} key={`sport${idx}`}>
										{displaySport}
									</MenuItem>
								);
							})}
						</Select>
					</FormControl>
				</Grid>

				<Grid item>
					<FormControl size='small' variant='outlined'>
						<InputLabel id='league-label'>League</InputLabel>
						<Select
							labelId='league-label'
							id='league'
							data-testid='games-filters-league'
							label='LEAGUE'
							name='league'
							value={league || ''}
							onChange={({ target: { value } }): object => dispatch(setLeagueFacet(value || null))}
							className={classes.select}>
							<MenuItem value='' key={'none'}>
								<em>None</em>
							</MenuItem>
							{leagues?.map(
								(leagueName: string, idx: number): JSX.Element => (
									<MenuItem value={leagueName} key={`league${idx}`}>
										{leagueName}
									</MenuItem>
								)
							)}
						</Select>
					</FormControl>
				</Grid>

				<Grid item>
					<FormGroup row>
						<TextField
							id='startDate'
							data-testid='games-filters-startDate'
							label='Start Date'
							type='date'
							value={startTime || ''}
							onChange={({ target: { value } }): object => dispatch(setStartTimeFacet(value || null))}
							InputLabelProps={{
								shrink: true
							}}
							variant='outlined'
							size='small'
							className={classes.date}
						/>
					</FormGroup>
				</Grid>

				<Grid item>
					<TextField
						id='untilDate'
						data-testid='games-filters-untilDate'
						label='Until Date'
						type='date'
						value={endTime || ''}
						onChange={({ target: { value } }): object => dispatch(setEndTimeFacet(value || null))}
						InputLabelProps={{
							shrink: true
						}}
						variant='outlined'
						size='small'
						className={classes.date}
					/>
				</Grid>

				<Grid item>
					<ButtonGroup variant='outlined'>
						<Button
							data-testid='games-filters-range-today'
							disableElevation
							onClick={(): object => dispatch(setTimesFacet({ startTime: dayjs().format('YYYY-MM-DD'), endTime: dayjs().format('YYYY-MM-DD') }))}
							className={`${classes.facetButton} ${classes.segmentedInputButton}`}>
							Today
						</Button>
						<Button
							data-testid='games-filters-range-tomorrow'
							disableElevation
							onClick={(): object =>
								dispatch(
									setTimesFacet({ startTime: dayjs().add(1, 'day').format('YYYY-MM-DD'), endTime: dayjs().add(1, 'day').format('YYYY-MM-DD') })
								)
							}
							className={`${classes.facetButton} ${classes.segmentedInputButton}`}>
							Tomorrow
						</Button>
						<Button
							data-testid='games-filters-range-week'
							disableElevation
							onClick={(): object =>
								dispatch(setTimesFacet({ startTime: dayjs().format('YYYY-MM-DD'), endTime: dayjs().add(1, 'week').format('YYYY-MM-DD') }))
							}
							className={`${classes.facetButton} ${classes.segmentedInputButton}`}>
							Week
						</Button>
					</ButtonGroup>
				</Grid>

				<Grid item>
					<Button
						data-testid='games-filters-hasairings'
						variant={hasAirings ? 'contained' : 'outlined'}
						color={hasAirings ? 'primary' : undefined}
						disableElevation
						onClick={(): object => dispatch(toggleHasAiringsFacet())}
						className={classes.facetButton}>
						{hasAirings && <CheckBoxIcon fontSize='medium' />}
						{!hasAirings && <CheckBoxOutlineBlankIcon fontSize='medium' />}
						Has Airings
					</Button>
				</Grid>

				<Grid item>
					<FormControl size='small' variant='outlined'>
						<InputLabel id='status-label'>Status</InputLabel>
						<Select
							labelId='status-label'
							id='status'
							data-testid='games-filters-status'
							label='STATUS'
							name='status'
							value={status || ''}
							onChange={({ target: { value } }): object => dispatch(setStatusFacet(value || null))}
							className={classes.select}>
							<MenuItem value=''>
								<em>None</em>
							</MenuItem>
							<MenuItem value='COMPLETED'>Completed</MenuItem>
							<MenuItem value='IN_PROGRESS'>In progress</MenuItem>
							<MenuItem value='POSTPONED'>Postponed</MenuItem>
							<MenuItem value='SCHEDULED'>Scheduled</MenuItem>
						</Select>
					</FormControl>
				</Grid>

				<Grid item>
					<FormControlLabel
						control={
							<Checkbox
								data-testid='games-filters-autorefetch'
								checked={autoRefetch}
								onChange={(): object => dispatch(toggleAutoRefetch())}
								color='primary'
							/>
						}
						label='Auto Refresh'
						className={classes.refetchToggle}
					/>
				</Grid>

				{hasFacets && (
					<Grid item>
						<Button
							data-testid='games-filters-clearfilters'
							variant='outlined'
							color='default'
							onClick={(): object => dispatch(resetAllFacets())}
							className={classes.facetButton}>
							<ClearIcon fontSize='medium' />
							Reset filters
						</Button>
					</Grid>
				)}
			</Grid>
			{hasReversedDates && (
				<Grid className={classes.filter}>
					<Alert severity='info'>
						<strong>Start</strong> and <strong>Until</strong> dates are reversed - the <strong>Until</strong> date will be ignored.
					</Alert>
				</Grid>
			)}
		</>
	);
};

export default ReportFilters;
